import { useParams } from 'react-router-dom'
import s from './DealEstimatingCosts.scss'
import { useState, useEffect } from 'react'
import { makeGetRequest, makePostRequest, makeDeleteRequest, makePutRequest } from 'utils/api'
import { Items } from './Items'
import { ITEM_TYPES } from './constants'
import { toItems, fromItems, toTemplates } from './serializers'
import { deleteItem } from './helpers'
import { useImmer } from 'use-immer'
import { useLoading, useUnsavedChanges } from 'hooks'
import cn from 'classnames'
import { KeyValuePairs, InputContainer } from 'components/ideal-core-ui'
import { getCurrency } from 'utils/authService'
import { NumericFormat } from 'react-number-format'
import { FaArrowRight, FaInfoCircle } from 'react-icons/fa'
import { UncontrolledTooltip, Button } from 'reactstrap'
import swal from 'sweetalert'
import cloneDeep from 'lodash/cloneDeep'
import Select from 'react-select'

const DealEstimatingCosts = ({ entity, createTemplatePageProps }) => {
  const [estimatingData, setEstimatingData] = useState(null)
  const [items, setItems] = useImmer([])
  const [itemCosts, setItemCosts] = useState([])
  const { id } = useParams()
  const [, withLoadingLocks] = useLoading()
  const [overheadPercent, setOverheadPercent] = useState(null)
  const [netProfitPercent, setNetProfitPercent] = useState(null)
  const [otherPercent, setOtherPercent] = useState(null)
  const [templates, setTemplates] = useState([])
  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const [prevEstimatedTemplate, setPrevEstimatedTemplate] = useState(null)

  const {
    saveTemplate,
    canSaveTemplate,
    clearModal: clearCreateTemplateModal,
    editedTemplate
  } = createTemplatePageProps || {}

  useUnsavedChanges(items ? fromItems(items, true).some(item => !item.pristine) : true, saveAll)

  useEffect(() => {
    if (entity === 'deal') {
      getData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const populateEstimateForm = (params, response) => {
    setEstimatingData(response)
    if (!params || params?.section === 'cost') {
      setItemCosts(toItems(response.itemCosts))
      if (params?.section === 'cost') {
        return
      }
    }
    setItems(toItems(response.items, params?.withPristine ? items : null))
  }

  if (editedTemplate && editedTemplate !== prevEstimatedTemplate) {
    populateEstimateForm(null, editedTemplate)
    setPrevEstimatedTemplate(editedTemplate)
  }

  const resetForm = async () => {
    await withLoadingLocks(makeDeleteRequest(`/v1/deal-estimating/${id}/clean`))
    setEstimatingData(null)
    setItems([])
    setItemCosts([])
    setOverheadPercent(null)
    setNetProfitPercent(null)
    setOtherPercent(null)
    setSelectedTemplate(null)
  }

  const getData = async params => {
    const response = await withLoadingLocks(makeGetRequest(`/v1/deal-estimating/${id}`))
    populateEstimateForm(params, response)

    const templates = await withLoadingLocks(makeGetRequest('/v1/deal-estimating/template'))
    setTemplates(toTemplates(templates))
  }

  const calculateData = async props => {
    const response = await withLoadingLocks(
      makePutRequest('/v1/deal-estimating/template/calculate', {
        ...(props?.estimatingData || estimatingData),
        items: fromItems(props?.items || items),
        itemCosts: []
      })
    )
    setEstimatingData(response)
    setItemCosts(toItems(response.itemCosts))
    setItems(toItems(response.items))
  }

  const addLineItem = async itemType => {
    let item = {
      itemType,
      name: '',
      value: 0,
      price: 0,
      totalItemValue: 0
    }
    let response

    if (entity) {
      response = await withLoadingLocks(makePostRequest(`/v1/deal-estimating/${id}/item`, item))
    } else {
      item.id = Date.now()
    }

    setItems({
      ...items,
      [itemType]: [
        ...(items[itemType] ? items[itemType] : []),
        { ...(entity ? response : item), pristine: true }
      ]
    })
  }

  const removeItem = async (itemType, itemId) => {
    const willRemove = await swal({
      title: 'Are you sure you want to delete this line item?',
      text: 'This action can’t be undone',
      buttons: ['Cancel', 'Yes'],
      icon: 'warning'
    })
    if (willRemove) {
      setItems(draft => {
        deleteItem(draft, itemType, itemId)
      })

      if (entity) {
        await makeDeleteRequest(`/v1/deal-estimating/${id}/item/${itemId}`)
        getData({ section: 'cost' })
      } else {
        const itemsClone = cloneDeep(items)
        deleteItem(itemsClone, itemType, itemId)
        calculateData({ items: itemsClone })
      }
    }
  }

  const changeProperty = (itemType, value, field, itemId) => {
    setItems(draft => {
      const item = draft[itemType].find(item => item.id === itemId)
      item[field] = value
      item.pristine = false
    })
  }

  const updateItem = async (itemId, itemType) => {
    const item = items[itemType].find(item => item.id === itemId)

    if (entity) {
      const response = await withLoadingLocks(
        makePutRequest(`/v1/deal-estimating/${id}/item/${itemId}`, {
          name: item.name,
          value: item.value,
          price: item.price
        })
      )

      setItems(draft => {
        const item = draft[itemType].find(item => item.id === itemId)
        item.pristine = true
        item.totalItemValue = response.items[0].totalItemValue
      })

      getData({ section: 'cost' })
    } else {
      calculateData()
    }
  }

  const updateCost = async (value, field) => {
    let val = value.target.value

    if (entity) {
      await withLoadingLocks(makePutRequest(`/v1/deal-estimating/${id}`, { [field]: val }))
      getData({ section: 'cost' })
    } else {
      calculateData({ estimatingData: { ...estimatingData, [field]: val } })
    }
  }

  async function saveAll() {
    if (entity) {
      const response = await withLoadingLocks(
        makePutRequest(`/v1/deal-estimating/${id}/items`, {
          items: fromItems(items)
        })
      )
      setEstimatingData(response)
      setItemCosts(toItems(response.itemCosts))
      setItems(toItems(response.items))
    } else {
      calculateData()
    }
  }

  const fetchTemplate = async templateId => {
    const template = await withLoadingLocks(
      makePutRequest(`/v1/deal-estimating/${id}/template/${templateId}`)
    )
    populateEstimateForm(null, template)
  }

  return (
    <>
      {templates.length > 0 && (
        <div className={s.templates}>
          <div>
            <span>Load Estimate Template: </span>
            <Select
              onChange={value => {
                setSelectedTemplate(value)
                fetchTemplate(value.value)
              }}
              options={templates}
              className={s.select}
              value={selectedTemplate}
            />
          </div>
          <div className={s.reset} onClick={resetForm}>
            reset estimate form
          </div>
        </div>
      )}
      <div className={s.costsWrapper}>
        <p className={cn(s.bold, s.underlined)}>
          Set Opportunity Markup: &nbsp;
          <FaInfoCircle id="markup" />
          <UncontrolledTooltip placement="top" target="markup">
            Set opportunity markup by defining overhead, net profit and other adjustments.
          </UncontrolledTooltip>
        </p>
        <p className={s.bold}>% Overhead</p>
        <InputContainer
          type="text"
          placeholder="%"
          cb={value => setOverheadPercent(value)}
          text={overheadPercent !== null ? overheadPercent : (estimatingData?.overheadPercent ?? 0)}
          onBlurCb={value => updateCost(value, 'overheadPercent')}
          classname={s.input}
          style={{ display: 'inline-block', width: '50px', marginRight: '10px' }}
        />
        <p className={s.bold}>% Net Profit</p>
        <InputContainer
          type="text"
          placeholder="%"
          cb={value => setNetProfitPercent(value)}
          text={
            netProfitPercent !== null ? netProfitPercent : (estimatingData?.netProfitPercent ?? 0)
          }
          onBlurCb={value => updateCost(value, 'netProfitPercent')}
          classname={s.input}
          style={{ display: 'inline-block', width: '50px', marginRight: '10px' }}
        />
        <p className={s.bold}>% Other</p>
        <InputContainer
          type="text"
          placeholder="%"
          cb={value => setOtherPercent(value)}
          text={otherPercent !== null ? otherPercent : (estimatingData?.otherPercent ?? 0)}
          onBlurCb={value => updateCost(value, 'otherPercent')}
          classname={s.input}
          style={{ display: 'inline-block', width: '50px', marginRight: '10px' }}
        />
        <div style={{ display: 'inline-block' }}>
          <FaArrowRight style={{ marginRight: 20 }} />
          <p className={s.bold}>Markup: {estimatingData?.markup ?? 0}%</p>&#8226;
          <p className={s.bold}>&nbsp;&nbsp;Gross Margin: {estimatingData?.grossMargin ?? 0}%</p>
        </div>
      </div>
      <div style={{ padding: 10 }}>
        <p className={s.bold}>Total Estimated Cost:</p>
        <KeyValuePairs
          style={{ display: 'inline-flex', position: 'relative', bottom: 17, marginRight: 20 }}
          pairs={[
            {
              style: { padding: 0 },
              key: '',
              value: (
                <NumericFormat
                  thousandSeparator
                  decimalScale={2}
                  value={estimatingData?.totalItemCosts ?? 0}
                  prefix={getCurrency().symbol}
                  displayType={'text'}
                />
              )
            }
          ]}
        />
        <p className={s.bold}>Overhead:</p>
        <KeyValuePairs
          style={{ display: 'inline-flex', position: 'relative', bottom: 17, marginRight: 20 }}
          pairs={[
            {
              style: { padding: 0 },
              key: '',
              value: (
                <NumericFormat
                  thousandSeparator
                  decimalScale={2}
                  value={estimatingData?.overheadValue ?? 0}
                  prefix={getCurrency().symbol}
                  displayType={'text'}
                />
              )
            }
          ]}
        />
        <p className={s.bold}>Net Profit:</p>
        <KeyValuePairs
          style={{ display: 'inline-flex', position: 'relative', bottom: 17, marginRight: 20 }}
          pairs={[
            {
              style: { padding: 0 },
              key: '',
              value: (
                <NumericFormat
                  thousandSeparator
                  decimalScale={2}
                  value={estimatingData?.netProfitValue ?? 0}
                  prefix={getCurrency().symbol}
                  displayType={'text'}
                />
              )
            }
          ]}
        />
        <p className={s.bold}>Other:</p>
        <KeyValuePairs
          style={{ display: 'inline-flex', position: 'relative', bottom: 17, marginRight: 20 }}
          pairs={[
            {
              style: { padding: 0 },
              key: '',
              value: (
                <NumericFormat
                  thousandSeparator
                  decimalScale={2}
                  value={estimatingData?.otherValue ?? 0}
                  prefix={getCurrency().symbol}
                  displayType={'text'}
                />
              )
            }
          ]}
        />
        <br />
        <p className={cn(s.bold, s.green)}>Total Costs + Markup:</p>
        <KeyValuePairs
          style={{ display: 'inline-flex', position: 'relative', bottom: 17, marginRight: 20 }}
          pairs={[
            {
              style: { padding: 0 },
              key: '',
              keyClass: [s.green],
              value: (
                <NumericFormat
                  thousandSeparator
                  decimalScale={2}
                  value={estimatingData?.costsWithMarkupSuggestedValue ?? 0}
                  prefix={getCurrency().symbol}
                  displayType={'text'}
                  style={{ color: '#4ca69a' }}
                />
              )
            }
          ]}
        />
        <FaArrowRight style={{ marginRight: 20 }} />
        <p className={cn(s.bold, s.green)}>Suggested Opportunity Value</p>
      </div>
      <hr style={{ marginTop: 0 }} />
      <div className={s.dealEstimatingItemsWrapper}>
        <>
          <Button className={s.saveBtn} onClick={saveAll}>
            Calculate All
          </Button>
          {ITEM_TYPES.map(itemType => (
            <div key={itemType.value}>
              <p className={s.title}>{itemType.label}</p>
              <Items
                changeProperty={(value, field, id) =>
                  changeProperty(itemType.value, value, field, id)
                }
                onAdd={() => addLineItem(itemType.value)}
                items={items}
                itemCosts={itemCosts}
                itemType={itemType}
                removeItem={id => removeItem(itemType.value, id)}
                updateItem={updateItem}
              />
            </div>
          ))}
        </>
      </div>
      {clearCreateTemplateModal && saveTemplate && (
        <>
          <hr style={{ marginTop: 0 }} />
          <div className={s.btnContainer}>
            <Button color="secondary" onClick={clearCreateTemplateModal}>
              Cancel
            </Button>
            <Button
              className={s.saveBtn}
              onClick={() => saveTemplate({ ...estimatingData, items })}
              disabled={fromItems(items, true).some(item => !item.pristine) || !canSaveTemplate}
            >
              Save Template
            </Button>
          </div>
        </>
      )}
    </>
  )
}

export default DealEstimatingCosts
